import React, { useMemo } from 'react';
import { DEGREE_LABELS } from '../../lib/theory';
import {
  INLAY_FRETS, DOUBLE_INLAY_FRETS,
  DOBRO_FORMULAS, buildDobroNodes, buildDobroChordNodes,
} from '../../lib/dobro-theory';
import { useIsDesktop, useIsLandscapePhone } from '../../hooks/useIsDesktop';

// ── Layout (boards 18 + 20, same look as the guitar) ──────────────────────────
//
// The SVG scales to its container, so every number below is a viewBox unit.
//
// PHONE is authored 1:1 with board 18: rendered at the board's 358px width the
// viewBox scale is ~1, so strings land 28px apart and dots are 21px (75% of the gap).
// Frets 0-12, equal-width frets (~26px each; the old taper crushed frets 8-12).
//
// DESKTOP is board 20: frets 0-15, equal-width frets (same as the phone),
// 36px dots on a 49.2px string gap (73%). The board is 880 wide so a typical main-area
// width (~900px) renders it near 1:1; it scales to fill any width.

const NUM_STRINGS = 6;
// Open G tuning low→high; string 0 renders at the bottom of the board, string 5 at the top
const STRING_LABELS = ['G', 'B', 'D', 'G', 'B', 'D'];

const PHONE = {
  frets: 12,
  left: 16,          // strings start here; the string names sit in the 0-12 gutter
  boardWidth: 338,   // fret 0 slot through the end of fret 12
  gap: 28,
  pad: 12,
  nutW: 4,
  noteR: 10.5,       // outer radius (21px dot incl. ring), 75% of the 28 string gap
  ring: 1.25,
  font: 9,
  fontSmall: 8,      // labels longer than 2 chars (e.g. "bb7")
  inlayR: 4,
  stringThick: [2.4, 2.1, 1.8, 1.5, 1.2, 1],
  nameX: 6,
  nameFont: 11,
  numFont: 11,
  numTop: 3,         // gap between the board bottom and the fret-number line
  numLine: 15,
  bottom: 20,        // room under the board for fret numbers
  viewLeft: 0,
  viewRightPad: 2,
};

// LANDSCAPE is a phone held sideways (useIsLandscapePhone): frets 0-15 like the desktop, equal
// frets (~52px) and a ~34px string gap (roomier than the phone, for bigger dots), so the whole
// neck fits in ~215px of height and the board needs no scrolling. Authored ~1:1 at a typical 812px-wide landscape screen.
const LANDSCAPE = {
  ...PHONE,
  frets: 15,
  boardWidth: 800,
  gap: 34,
  noteR: 13,         // 26px dot incl. ring, 76% of the 34 string gap
  font: 11,
  fontSmall: 10,
  inlayR: 4.5,
  nameFont: 12,
  numFont: 12,
};

const DESKTOP = {
  frets: 15,
  left: 0,
  boardWidth: 880,
  gap: 49.2,
  pad: 27,
  nutW: 5,
  noteR: 18,         // 36px dot incl. ring, 73% of the gap
  ring: 1.25,
  font: 13,
  fontSmall: 11,
  inlayR: 5,
  stringThick: [3, 2.6, 2.2, 1.8, 1.4, 1.1],
  nameX: -11,
  nameFont: 13,
  numFont: 13,
  numTop: 10,
  numLine: 18,
  bottom: 30,
  viewLeft: -22,     // gutter for the open-string names
  viewRightPad: 0,
};

/**
 * Slot widths (viewBox units) for fret 0..frets. Slot 0 is the open-string zone left of the nut.
 * Every slot is the same width, on every layout (Oct 2026): a real-neck taper squeezed the high
 * frets narrower than the dots on phones, and desktop was switched to match.
 */
function fretWidths(L) {
  return Array.from({ length: L.frets + 1 }, () => L.boardWidth / (L.frets + 1));
}

function fretPositions(widths, left) {
  const positions = [left];
  for (let i = 1; i < widths.length; i++) {
    positions.push(positions[i - 1] + widths[i - 1]);
  }
  return positions;
}

function resolveScaleFormula(type, tonality, modeName = 'Ionian') {
  if (type === 'Modes') return DOBRO_FORMULAS[modeName] ?? DOBRO_FORMULAS['Ionian'];
  const isMajor = tonality === 'Major';
  if (type === 'Pentatonic') return DOBRO_FORMULAS[isMajor ? 'Maj Pentatonic' : 'Min Pentatonic'];
  return DOBRO_FORMULAS[isMajor ? 'Major' : 'Minor'];
}

// The 12th fret has a double dot but is missing from dobro-theory's INLAY_FRETS, so add it here.
const ALL_INLAY_FRETS = [...new Set([...INLAY_FRETS, ...DOUBLE_INLAY_FRETS])].sort((a, b) => a - b);

export default function DobroFretboard({
  dobroKey,
  dobroTonality,
  dobroCategory,    // 'SCALES' | 'CHORDS'
  dobroType,
  dobroMode,        // diatonic mode name, used when dobroType === 'Modes'
  dobroShape,
  dobroStringFocus,
  dobroFilter,
  dobroLabel,
}) {
  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscapePhone();
  const L = isDesktop ? DESKTOP : isLandscape ? LANDSCAPE : PHONE;
  const DISPLAY_FRETS = L.frets;

  const activeNodes = useMemo(() => {
    if (dobroCategory === 'SCALES') {
      return buildDobroNodes(dobroKey, resolveScaleFormula(dobroType, dobroTonality, dobroMode), dobroStringFocus, dobroTonality);
    }
    if (dobroCategory === 'CHORDS') {
      return buildDobroChordNodes(dobroKey, dobroShape, dobroTonality, dobroStringFocus);
    }
    return [];
  }, [dobroKey, dobroTonality, dobroCategory, dobroType, dobroMode, dobroShape, dobroStringFocus]);

  // The Filter quick button is greyed for Chords (every chord tone always shows), so it is ignored there.
  const visibleNodes = useMemo(() => {
    if (dobroCategory === 'CHORDS') return activeNodes;
    if (dobroFilter === 'Roots Only')  return activeNodes.filter(n => n.isRoot);
    if (dobroFilter === 'Triads Only') return activeNodes.filter(n => [0, 4, 7, 3].includes(n.interval));
    return activeNodes;
  }, [activeNodes, dobroFilter, dobroCategory]);

  // Geometry
  const widths     = useMemo(() => fretWidths(L), [L]);
  const xPositions = useMemo(() => fretPositions(widths, L.left), [widths, L]);
  const right      = xPositions[DISPLAY_FRETS] + widths[DISPLAY_FRETS]; // end of the last fret
  const nutX       = xPositions[1];

  const neckH = L.pad * 2 + (NUM_STRINGS - 1) * L.gap;
  const midY  = neckH / 2;
  const viewW = right + L.viewRightPad - L.viewLeft;
  const viewH = neckH + L.bottom;

  // string 0 (lowest pitch, G) at BOTTOM; string 5 (highest, D) at TOP
  const stringY = (sIdx) => L.pad + (NUM_STRINGS - 1 - sIdx) * L.gap;
  const noteX   = (fretIdx) => xPositions[fretIdx] + widths[fretIdx] / 2;

  const noteLabel = (node) => {
    if (dobroLabel === 'Intervals')  return DEGREE_LABELS[node.interval] ?? '';
    if (dobroLabel === 'Note Names') return node.noteName;
    return '';
  };

  const inlayFrets = ALL_INLAY_FRETS.filter(f => f <= DISPLAY_FRETS);

  // One note dot: roots are solid terracotta with a white label; scale tones are
  // white with a sage ring and an ink label. Flat — the boards have no shadow.
  const renderDot = (node) => {
    const cx = noteX(node.fret);
    const cy = stringY(node.string);
    const label = noteLabel(node);
    return (
      <g
        key={`dobro-note-${node.string}-${node.fret}-${dobroKey}-${dobroCategory}`}
        className="animate-note-spring"
        style={{ animationDelay: `${node.fret * 15}ms` }}
      >
        <circle
          cx={cx} cy={cy} r={L.noteR - L.ring / 2}
          strokeWidth={L.ring}
          style={{
            fill:   node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
            stroke: node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
          }}
        />
        {label !== '' && label != null && (
          <text
            x={cx} y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={String(label).length > 2 ? L.fontSmall : L.font}
            fontWeight={800}
            className="pointer-events-none"
            style={{ fill: node.isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="w-full">
      <svg
        viewBox={`${L.viewLeft} 0 ${viewW} ${viewH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="select-none"
      >
        {/* Board surface with hairline top/bottom edges */}
        <rect x={nutX} y={0} width={right - nutX} height={neckH} style={{ fill: 'hsl(var(--board))' }} />
        <rect x={nutX} y={0} width={right - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={nutX} y={neckH - 1} width={right - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />

        {/* Inlay dots */}
        {inlayFrets.map(f => {
          const cx = noteX(f);
          const dotStyle = { fill: 'hsl(var(--inlay))' };
          if (DOUBLE_INLAY_FRETS.includes(f)) {
            return (
              <g key={`inlay-${f}`}>
                <circle cx={cx} cy={midY - L.gap} r={L.inlayR} style={dotStyle} />
                <circle cx={cx} cy={midY + L.gap} r={L.inlayR} style={dotStyle} />
              </g>
            );
          }
          return <circle key={`inlay-${f}`} cx={cx} cy={midY} r={L.inlayR} style={dotStyle} />;
        })}

        {/* Nut — solid ink, flush against the left of fret 1 */}
        <rect x={nutX - L.nutW} y={0} width={L.nutW} height={neckH} style={{ fill: 'hsl(var(--string))' }} />

        {/* Fret wires — thin grey; the last one closes the end of the board */}
        {Array.from({ length: DISPLAY_FRETS - 1 }, (_, i) => i + 2).concat(DISPLAY_FRETS + 1).map(f => {
          const x = f > DISPLAY_FRETS ? right : xPositions[f];
          return (
            <rect
              key={`fret-${f}`}
              x={x - 0.5} y={0} width={1} height={neckH}
              style={{ fill: 'hsl(var(--fret))' }}
            />
          );
        })}

        {/* Strings — solid ink, thicker toward the low G; muted strings fade */}
        {STRING_LABELS.map((_, s) => {
          const thick = L.stringThick[s] ?? 1.5;
          return (
            <rect
              key={`string-${s}`}
              x={L.left} y={stringY(s) - thick / 2}
              width={right - L.left} height={thick}
              style={{ fill: 'hsl(var(--string))' }}
              opacity={dobroStringFocus[s] ? 1 : 0.2}
            />
          );
        })}

        {/* Open-string names — left of the nut, clear of the open-string note dots */}
        {STRING_LABELS.map((name, s) => (
          <text
            key={`string-label-${s}`}
            x={L.nameX}
            y={stringY(s)}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fill: 'hsl(var(--ink-muted))' }}
            fontSize={L.nameFont}
            fontWeight={700}
          >
            {name}
          </text>
        ))}

        {/* Note markers */}
        {visibleNodes.filter(node => node.fret <= DISPLAY_FRETS).map(renderDot)}

        {/* Fret numbers — under the board; inlay frets are bold ink, the rest muted */}
        {Array.from({ length: DISPLAY_FRETS + 1 }, (_, i) => i).map(f => {
          const marked = inlayFrets.includes(f);
          return (
            <text
              key={`fnum-${f}`}
              x={noteX(f)} y={neckH + L.numTop + L.numLine / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fill: marked ? 'hsl(var(--ink))' : 'hsl(var(--ink-muted))' }}
              fontSize={L.numFont}
              fontWeight={marked ? 800 : 600}
              opacity={marked ? 1 : 0.75}
              className="select-none"
            >
              {f}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
